import { MESH_PROTOCOL_VERSION } from './types.js';
import { PendingToolCallTracker, newCallId } from './tools.js';
import { findPeersByTool } from './routing.js';
const TOOL_CALL_RX = /<tool_call>\s*([\s\S]*?)\s*<\/tool_call>/gi;
/**
 * Parse every `<tool_call>{"name": "...", "arguments": {...}}</tool_call>`
 * block in `text`. Tolerant of surrounding whitespace; silently skips
 * malformed blocks (a partial/garbled block from mid-stream detokenization
 * self-corrects on the next chunk) rather than throwing — same discipline as
 * `decodeStageControl`.
 */
export function parseToolCalls(text) {
    const calls = [];
    TOOL_CALL_RX.lastIndex = 0;
    let m;
    while ((m = TOOL_CALL_RX.exec(text)) !== null) {
        const body = m[1].trim();
        try {
            const parsed = JSON.parse(body);
            if (typeof parsed.name === 'string' &&
                parsed.name.length > 0 &&
                parsed.arguments &&
                typeof parsed.arguments === 'object' &&
                !Array.isArray(parsed.arguments)) {
                calls.push({ name: parsed.name, args: parsed.arguments, raw: m[0] });
            }
        }
        catch {
            // malformed block — skip, the next detokenized chunk may complete it
        }
    }
    return calls;
}
/** The first well-formed tool call in `text`, or `null`. Convenience for the
 * single-round-trip driver path (this PR's scope). */
export function firstToolCall(text) {
    return parseToolCalls(text)[0] ?? null;
}
/** Format a `MeshToolResult` (or an error) as a `<tool_result>` block for the
 * driver to re-prefill into the model's context. Mirrors `useDirector`'s
 * `<tool_result>` convention so a model prompted for either interoperates. */
export function buildToolResultBlock(toolName, result) {
    const payload = result.status === 'ok'
        ? { name: toolName, result: result.result?.content ?? result.result ?? null }
        : { name: toolName, error: result.error ?? `tool ${result.status}` };
    return `<tool_result>\n${JSON.stringify(payload)}\n</tool_result>`;
}
/** Rank tool providers: priorityScore desc, then freshest lastSeen, then
 * peerId asc (determinism). */
function rankProviders(providers, priorityScore) {
    return [...providers].sort((a, b) => {
        const pa = priorityScore(a.peerId);
        const pb = priorityScore(b.peerId);
        if (pa !== pb)
            return pb - pa;
        if (a.lastSeen !== b.lastSeen)
            return b.lastSeen - a.lastSeen;
        return a.peerId < b.peerId ? -1 : a.peerId > b.peerId ? 1 : 0;
    });
}
/**
 * Route ONE tool call to a live provider and return its result. Tries each
 * provider (ranked) in turn until one answers within `timeoutMs`; a
 * disappearing/silent provider simply falls through to the next, and an
 * empty/exhausted provider set is a graceful `no-provider`/`timeout`.
 *
 * Wire correlation reuses `PendingToolCallTracker` exactly as the docstring
 * on `tools.ts` prescribes — this function owns a private tracker + its own
 * `onTool` subscription for the duration of the round trip, so it composes
 * cleanly alongside a stage session's own tracker without cross-talk (result
 * frames are keyed by a freshly-minted `callId`).
 */
export async function runToolRoundTrip(opts) {
    const timeoutMs = opts.timeoutMs ?? 30_000;
    const priorityScore = opts.priorityScore ?? (() => 0);
    const now = opts.now ?? (() => Date.now());
    const selfId = opts.excludePeerId ?? opts.peer.selfId;
    const providers = rankProviders(findPeersByTool(opts.roster, opts.call.name, { excludePeerId: selfId }), priorityScore);
    const triedPeerIds = [];
    if (providers.length === 0) {
        return { status: 'no-provider', error: `no peer advertises tool "${opts.call.name}"`, triedPeerIds };
    }
    const tracker = new PendingToolCallTracker();
    const unsub = opts.peer.onTool((frame) => {
        if (frame.kind === 'result')
            tracker.settle(frame);
    });
    try {
        for (const provider of providers) {
            triedPeerIds.push(provider.peerId);
            const callId = newCallId();
            const callFrame = {
                kind: 'call',
                v: MESH_PROTOCOL_VERSION,
                ts: now(),
                callId,
                toolName: opts.call.name,
                args: opts.call.args,
            };
            let result;
            try {
                const waiter = tracker.expect(callId, timeoutMs);
                await opts.peer.sendTool(callFrame, provider.peerId);
                result = await waiter;
            }
            catch {
                // timeout or send failure — this provider vanished; try the next.
                continue;
            }
            // A provider answered. Record economy: provider credited on success,
            // self debited for consuming regardless (occupied the provider's turn).
            if (opts.standingLedger) {
                const t = now();
                opts.standingLedger.recordToolService({ providerPeerId: provider.peerId, toolName: opts.call.name, succeeded: result.status === 'ok' }, t);
                opts.standingLedger.recordToolConsumption({ consumerPeerId: opts.peer.selfId, toolName: opts.call.name }, t);
            }
            return {
                status: result.status,
                providerPeerId: provider.peerId,
                result,
                resultBlock: buildToolResultBlock(opts.call.name, result),
                ...(result.status !== 'ok' ? { error: result.error ?? `tool ${result.status}` } : {}),
                triedPeerIds,
            };
        }
        // Every provider timed out / failed to send.
        return {
            status: 'timeout',
            error: `no provider answered tool "${opts.call.name}" within ${timeoutMs}ms (tried ${triedPeerIds.length})`,
            triedPeerIds,
        };
    }
    finally {
        unsub();
        tracker.abortAll('tool round-trip finished');
    }
}
